import { useEffect, useEffectEvent } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { formatTimeForAPI, parseTimeFromAPI, stanAnalytics, stanNotify } from '@/lib';
import { useUser } from '@/queries/useUser';
import { FLOW_TRIGGER } from '../constants';
import { createEditFlowSchema, type EditFlowFormData } from '../validations/editFlow';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import type { UpdateFlowPayload } from '../types/editFlow.types';
import { useUpdateFlow } from '../queries';

interface UseEditFlowFormProps {
    flow: FlowSummary | null;
    isOpen: boolean;
    onClose: () => void;
}

export const useEditFlowForm = ({ flow, isOpen, onClose }: UseEditFlowFormProps) => {
    const { user } = useUser();
    const updateFlowMutation = useUpdateFlow();

    const selectedTrigger = flow?.trigger || flow?.trigger_type || FLOW_TRIGGER.CHECKOUT;
    const isDateTriggerActive = selectedTrigger === FLOW_TRIGGER.TIME;
    const isCartAbandonedTriggerActive = selectedTrigger === FLOW_TRIGGER.CART_ABANDONMENT;
    const isPurchaseTriggerActive = selectedTrigger === FLOW_TRIGGER.CHECKOUT;

    // Calculate default values from flow data if available
    const getDefaultValues = () => {
        if (!flow) {
            return {
                flowName: '',
                selectedProducts: [],
                selectedExcludeProducts: [],
                showRemoveRecipients: false,
                selectedDate: new Date(),
                selectedTime: ((new Date().getHours() + 1) % 24) * 60,
                selectedTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        }

        const flowData = flow.data || {};
        return {
            flowName: flowData.name || '',
            selectedProducts: flowData.filters?.page_id_list || [],
            selectedExcludeProducts: flowData.filters?.exclude_page_id_list || [],
            showRemoveRecipients: (flowData.filters?.exclude_page_id_list?.length || 0) > 0,
            selectedDate:
                flowData.specific_time?.date && typeof flowData.specific_time.date === 'string'
                    ? parseISO(flowData.specific_time.date)
                    : new Date(),
            selectedTime: flowData.specific_time?.time
                ? parseTimeFromAPI(flowData.specific_time.time)
                : ((new Date().getHours() + 1) % 24) * 60,
            selectedTimezone: flowData.specific_time?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        };
    };

    const form = useForm<EditFlowFormData>({
        resolver: zodResolver(createEditFlowSchema(isDateTriggerActive)),
        mode: 'onChange',
        defaultValues: getDefaultValues(),
    });

    const handleFormReset = useEffectEvent((flowData: typeof flow) => {
        if (flowData) {
            const data = flowData.data || {};

            const parsedTime = data.specific_time?.time
                ? parseTimeFromAPI(data.specific_time.time)
                : ((new Date().getHours() + 1) % 24) * 60;

            form.reset({
                flowName: data.name || '',
                selectedProducts: data.filters?.page_id_list || [],
                selectedExcludeProducts: data.filters?.exclude_page_id_list || [],
                showRemoveRecipients: (data.filters?.exclude_page_id_list?.length || 0) > 0,
                selectedDate:
                    data.specific_time?.date && typeof data.specific_time.date === 'string'
                        ? parseISO(data.specific_time.date)
                        : new Date(),
                selectedTime: parsedTime,
                selectedTimezone: data.specific_time?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            });
        }
    });

    useEffect(() => {
        if (flow && isOpen) {
            handleFormReset(flow);
        }
    }, [flow, isOpen]);

    const onSubmit = async (data: EditFlowFormData) => {
        if (!flow) return;

        const updateData: UpdateFlowPayload = {
            flow_id: flow.flow_id,
            status: 'published',
            type: flow.type,
            trigger: selectedTrigger,
            data: {
                name: data.flowName,
            },
        };

        if (isPurchaseTriggerActive || isCartAbandonedTriggerActive) {
            const filters: UpdateFlowPayload['data']['filters'] = {
                page_id_list: data.selectedProducts || [],
            };

            if (data.showRemoveRecipients && data.selectedExcludeProducts && data.selectedExcludeProducts.length > 0) {
                filters.exclude_page_id_list = data.selectedExcludeProducts;
            }

            if (isCartAbandonedTriggerActive) {
                filters.exclude_page_id_list = data.selectedProducts;
            }

            updateData.data.filters = filters;
        }

        if (isDateTriggerActive && data.selectedDate) {
            updateData.data.specific_time = {
                tags: flow.data?.specific_time?.tags || [],
                date: format(data.selectedDate, 'yyyy-MM-dd'),
                time: formatTimeForAPI(data.selectedTime || 0),
                timezone: data.selectedTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
            };
        }

        updateFlowMutation.mutate(updateData, {
            onSuccess: () => {
                stanNotify({
                    type: 'success',
                    title: 'Flow Updated',
                });
                stanAnalytics('flow-updated', {
                    meta: { user_id: user?.user_id, username: user?.username },
                    props: { flow_id: flow?.flow_id },
                });
                onClose();
            },
            onError: (error: any) => {
                const errorMessage = error?.response?.data?.message || 'Failed to update flow';
                const is409Error = error?.response?.status === 409;

                stanNotify({
                    type: 'error',
                    title: is409Error ? 'Wait up!' : 'Update Failed',
                    text: errorMessage,
                });
            },
        });
    };

    return {
        form,
        onSubmit,
        isDateTriggerActive,
        isCartAbandonedTriggerActive,
        isPurchaseTriggerActive,
        selectedTrigger,
        isSubmitting: updateFlowMutation.isPending,
    };
};
